import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  TrendingUp, Users, AlertCircle, IndianRupee,
  ArrowUpRight, UserCheck, Target, Phone, CalendarClock, CalendarCheck2,
  ChevronDown, ChevronUp, Loader2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import { formatINR as fmt } from "../utils/format";

// ─── HELPERS ────────────────────────────────────────────────────────────────

const compact = (n) => {
  n = n || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000)     return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${Math.round(n)}`;
};

const fmtDateShort = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const fmtDateLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
};

// ─── STAGE CONFIG ────────────────────────────────────────────────────────────

const STAGES = [
  { key: "token",        label: "Token",     bg: "#dbeafe", fg: "#1d4ed8" },
  { key: "part_payment", label: "Part Paid", bg: "#fef3c7", fg: "#92400e" },
  { key: "full_paid",    label: "Full Paid", bg: "#dcfce7", fg: "#166534" },
  { key: "carryforward", label: "CF",        bg: "#ede9fe", fg: "#6d28d9" },
  { key: "adjusted",     label: "Adjusted",  bg: "#e0e7ff", fg: "#3730a3" },
  { key: "dropped",      label: "Dropped",   bg: "#fee2e2", fg: "#991b1b" },
  { key: "refund",       label: "Refund",    bg: "#ffe4e6", fg: "#9f1239" },
  { key: "pending_base", label: "Pending",   bg: "#f3f4f6", fg: "#4b5563" },
];

const EVENT_STATUS_STYLE = {
  Active:    { bg: "#dcfce7", fg: "#15803d", label: "Active" },
  Upcoming:  { bg: "#dbeafe", fg: "#1d4ed8", label: "Upcoming" },
  Completed: { bg: "#f3f4f6", fg: "#6b7280", label: "Done" },
};

// ─── TONE MAPS ────────────────────────────────────────────────────────────────

const TONE_BG = {
  info: "var(--info-bg)", success: "var(--success-bg)",
  warning: "var(--warning-bg)", danger: "var(--danger-bg)",
  gray: "var(--surface-muted)", primary: "var(--primary-light)",
};
const TONE_FG = {
  info: "var(--info)", success: "var(--success)",
  warning: "var(--warning)", danger: "var(--danger)",
  gray: "var(--text-muted)", primary: "var(--primary)",
};

// ─── KPI TILE ────────────────────────────────────────────────────────────────

function KpiTile({ icon, tone, label, value, sub, subTone, testid, onClick, children }) {
  return (
    <div
      className="kpi-card"
      data-testid={testid}
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      <div className="kpi-top">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="kpi-icon" style={{ background: TONE_BG[tone], color: TONE_FG[tone] }}>{icon}</div>
          <span className="kpi-label">{label}</span>
        </div>
        {onClick && <div className="kpi-arrow"><ArrowUpRight size={14} /></div>}
      </div>
      <div className="kpi-foot">
        <div className="kpi-value">{value}</div>
        {sub != null && <span className={`badge badge-${subTone || "gray"}`}>{sub}</span>}
      </div>
      {children}
    </div>
  );
}

// ─── STAGE STRIP (inside Total Leads) ────────────────────────────────────────

function StageStrip({ byStage }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
      {STAGES.map(({ key, label, bg, fg }) => {
        const count = byStage?.[key] ?? 0;
        return (
          <span key={key} style={{
            fontSize: "0.68rem", fontWeight: 600, padding: "2px 9px",
            borderRadius: 999, background: bg, color: fg,
          }}>
            {label} {count}
          </span>
        );
      })}
    </div>
  );
}

// ─── CALLS 7-DAY BAR CHART ───────────────────────────────────────────────────

function Calls7DayCard({ calls7d }) {
  const hasData = calls7d?.some((d) => d.count > 0);
  const chartData = (calls7d || []).map((d) => ({
    label: fmtDateShort(d.date),
    count: d.count,
  }));

  return (
    <div className="ap-card" data-testid="calls-7d-chart">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <Phone size={16} style={{ color: "var(--accent)" }} />
        <h3 style={{ margin: 0, fontSize: 15 }}>Calls — Last 7 Days</h3>
      </div>
      {!hasData ? (
        <div style={{ textAlign: "center", padding: "28px 0", color: "var(--text-muted)", fontSize: 13 }}>
          No Callyzer data synced yet
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={140}>
          <BarChart data={chartData} barCategoryGap="35%" margin={{ top: 2, right: 4, left: -16, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip
              cursor={{ fill: "var(--surface-muted)" }}
              formatter={(v) => [v, "Calls"]}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid var(--border)" }}
            />
            <Bar dataKey="count" fill="var(--accent)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── ACTIVE EVENTS MINI-CARDS ────────────────────────────────────────────────

function ActiveEventCards({ events }) {
  if (!events?.length) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        fontSize: "0.7rem", fontWeight: 700, color: "var(--text-muted)",
        textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10,
      }}>
        Events
      </div>
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 10,
      }}>
        {events.map((e) => {
          const s = EVENT_STATUS_STYLE[e.status] || EVENT_STATUS_STYLE.Upcoming;
          const progress = e.total_leads ? Math.round((e.full_paid / e.total_leads) * 100) : 0;
          return (
            <div
              key={e.id}
              data-testid={`event-card-${e.id}`}
              style={{
                flexShrink: 0, width: 190, padding: "12px 14px",
                background: "var(--surface)", border: "1px solid var(--border)",
                borderRadius: 12,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.3, flex: 1 }}>
                  {e.name}
                </span>
                <span style={{
                  fontSize: 10, fontWeight: 600, flexShrink: 0,
                  padding: "2px 7px", borderRadius: 999, background: s.bg, color: s.fg,
                }}>
                  {s.label}
                </span>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
                {fmtDateShort(e.start_date)}{e.end_date ? ` – ${fmtDateShort(e.end_date)}` : ""}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 5 }}>
                <span style={{ color: "#16a34a", fontWeight: 600 }}>{e.full_paid} full paid</span>
                <span style={{ color: "var(--text-muted)" }}>{e.total_leads} total</span>
              </div>
              <div style={{ height: 5, background: "var(--border)", borderRadius: 99 }}>
                <div style={{ height: "100%", width: `${progress}%`, background: "#10b981", borderRadius: 99, transition: "width 0.4s" }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── EVENT BREAKDOWN TABLE ───────────────────────────────────────────────────

function EventBreakdownTable({ items }) {
  if (!items?.length) {
    return (
      <div className="ap-card" data-testid="event-breakdown">
        <h3 style={{ margin: "0 0 10px", fontSize: 15 }}>Event Breakdown</h3>
        <div className="empty-state">No active events to display.</div>
      </div>
    );
  }
  return (
    <div className="ap-card" data-testid="event-breakdown">
      <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>Event Breakdown</h3>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Total</th>
              <th>Token</th>
              <th>Part Paid</th>
              <th>Full Paid</th>
              <th>Overdue EMIs</th>
              <th style={{ textAlign: "right" }}>%</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.event} data-testid={`event-row-${e.event}`}>
                <td style={{ fontWeight: 600 }}>{e.event}</td>
                <td>{e.total}</td>
                <td>
                  <span style={{ background: "#dbeafe", color: "#1d4ed8", fontSize: 11, padding: "2px 8px", borderRadius: 999 }}>
                    {e.token}
                  </span>
                </td>
                <td>
                  <span style={{ background: "#fef3c7", color: "#92400e", fontSize: 11, padding: "2px 8px", borderRadius: 999 }}>
                    {e.part_payment}
                  </span>
                </td>
                <td>
                  <span style={{ background: "#dcfce7", color: "#166534", fontSize: 11, padding: "2px 8px", borderRadius: 999 }}>
                    {e.full_paid}
                  </span>
                </td>
                <td>
                  {e.overdue_emis > 0
                    ? <span style={{ background: "#fee2e2", color: "#991b1b", fontSize: 11, padding: "2px 8px", borderRadius: 999 }}>{e.overdue_emis}</span>
                    : <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>}
                </td>
                <td style={{
                  fontWeight: 700, textAlign: "right",
                  color: e.progress >= 80 ? "#15803d" : e.progress >= 50 ? "#d97706" : "var(--text-primary)",
                }}>
                  {e.progress}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── LEAD MINI ROW (in priority section) ─────────────────────────────────────

function LeadMiniRow({ lead }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "7px 0",
      borderBottom: "1px solid var(--border)",
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {lead.name || "—"}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
          {lead.phone && <span style={{ fontWeight: 600 }}>{lead.phone}</span>}
          {lead.phone && lead.program_name && " · "}
          {lead.program_name}
        </div>
      </div>
      {lead.call_status && (
        <span style={{
          flexShrink: 0, fontSize: "0.65rem", fontWeight: 600,
          padding: "2px 8px", borderRadius: 999,
          background: "var(--surface-muted)", color: "var(--text-muted)",
        }}>
          {lead.call_status}
        </span>
      )}
      {lead.via === "emi" && (
        <span
          data-testid="dashboard-emi-due-badge"
          style={{
            flexShrink: 0, fontSize: "0.65rem", fontWeight: 700,
            padding: "2px 8px", borderRadius: 999,
            background: "#fef3c7", color: "#92400e",
          }}
        >
          {lead.emi_count > 1 ? `${lead.emi_count} EMIs` : "EMI"} due
        </span>
      )}
    </div>
  );
}

// ─── PRIORITY SECTION ────────────────────────────────────────────────────────

const PRIORITY_LIMIT = 6;

function PrioritySection({ todayFollowups, upcomingFollowups }) {
  const [showAll, setShowAll] = useState(false);
  const isLoading = todayFollowups == null && upcomingFollowups == null;
  const totalToday = todayFollowups?.length || 0;
  const totalUpcoming = upcomingFollowups?.reduce((s, g) => s + g.count, 0) || 0;
  const total = totalToday + totalUpcoming;

  const header = (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
      <CalendarClock size={16} style={{ color: "var(--accent)" }} />
      <h3 style={{ margin: 0, fontSize: 15 }}>Priority Follow-ups</h3>
    </div>
  );

  if (isLoading) {
    return (
      <div className="ap-card" data-testid="priority-section" style={{ marginTop: 0 }}>
        {header}
        <div
          data-testid="priority-loader"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "24px 0", color: "var(--text-muted)", fontSize: 13 }}
        >
          <Loader2 size={16} className="spin-icon" /> Loading follow-ups…
        </div>
      </div>
    );
  }

  if (!total) {
    return (
      <div className="ap-card" data-testid="priority-section" style={{ marginTop: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <CalendarCheck2 size={16} style={{ color: "var(--accent)" }} />
          <h3 style={{ margin: 0, fontSize: 15 }}>Priority Follow-ups</h3>
        </div>
        <div className="empty-state" style={{ paddingTop: 8 }}>No follow-ups in the next 10 days</div>
      </div>
    );
  }

  // Cap visible leads at PRIORITY_LIMIT (Today first, then upcoming chronologically)
  const cap = showAll ? Infinity : PRIORITY_LIMIT;
  let rendered = 0;
  const todayVisible = (todayFollowups || []).slice(0, cap);
  rendered += todayVisible.length;
  const upcomingVisible = [];
  if (rendered < cap) {
    for (const g of upcomingFollowups || []) {
      const leads = g.leads.slice(0, cap - rendered);
      if (leads.length) upcomingVisible.push({ ...g, leads });
      rendered += leads.length;
      if (rendered >= cap) break;
    }
  }

  return (
    <div className="ap-card" data-testid="priority-section" style={{ marginTop: 0 }}>
      {header}

      {/* Today */}
      {todayVisible.length > 0 && (
        <div style={{ marginBottom: 16 }} data-testid="today-followups">
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999,
            background: "#fef3c7", color: "#92400e", marginBottom: 8,
          }}>
            Today · {totalToday} lead{totalToday > 1 ? "s" : ""}
          </div>
          {todayVisible.map((l, i) => <LeadMiniRow key={i} lead={l} />)}
        </div>
      )}

      {/* Upcoming 10 days */}
      {upcomingVisible.map((group) => (
        <div key={group.date} style={{ marginBottom: 12 }} data-testid={`upcoming-group-${group.date}`}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999,
            background: "#dbeafe", color: "#1e40af", marginBottom: 8,
          }}>
            {fmtDateLabel(group.date)} · {group.count} lead{group.count > 1 ? "s" : ""}
          </div>
          {group.leads.map((l, i) => <LeadMiniRow key={i} lead={l} />)}
        </div>
      ))}

      {/* More / less toggle */}
      {total > PRIORITY_LIMIT && (
        <button
          type="button"
          className="priority-more-btn"
          data-testid="priority-more-toggle"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? <>Show less <ChevronUp size={14} /></>
            : <>Show {total - PRIORITY_LIMIT} more <ChevronDown size={14} /></>}
        </button>
      )}
    </div>
  );
}

// ─── PERFORMER FUNNEL ────────────────────────────────────────────────────────

const FUNNEL_STAGES = [
  { key: "New Lead",         label: "New",       color: "#94a3b8" },
  { key: "Contacted",        label: "Contacted", color: "#3b82f6" },
  { key: "Interested",       label: "Interested",color: "#f97316" },
  { key: "Token Collected",  label: "Converted", color: "#10b981" },
];
const DONUT = ["#6366f1", "#10b981", "#f59e0b", "#3b82f6", "#8b5cf6", "#ec4899"];

function ShareDonut({ title, data, centerLabel, centerSub }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const rows = data.map((d) => ({ ...d, pct: Math.round((d.value / total) * 100) }));
  return (
    <div className="ap-card" data-testid="dash-share-donut">
      <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>{title}</h3>
      {data.length === 0 ? <div className="empty-state">No data yet.</div> : (
        <>
          <div style={{ position: "relative", height: 168 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rows} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  innerRadius={52} outerRadius={78} paddingAngle={2} stroke="none">
                  {rows.map((_, i) => <Cell key={i} fill={DONUT[i % DONUT.length]} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 18 }}>{centerLabel}</div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{centerSub}</div>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            {rows.map((d, i) => (
              <div key={i} className="donut-legend-row">
                <span style={{ display: "flex", alignItems: "center", gap: 8, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <span className="donut-dot" style={{ background: DONUT[i % DONUT.length] }} />{d.name}
                </span>
                <strong>{d.pct}%</strong>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── PERFORMER DASHBOARD ─────────────────────────────────────────────────────

function PerformerDashboard({ data, user }) {
  const funnelData = FUNNEL_STAGES.map((s) => ({
    name: s.label, value: (data.status_breakdown && data.status_breakdown[s.key]) || 0,
  })).filter((d) => d.value > 0);

  return (
    <div className="page-content" data-testid="dashboard-page-performer">
      <div className="page-header">
        <div>
          <h1 className="page-title">Performer Dashboard</h1>
          <p className="page-sub">Welcome back, {user?.name?.split(" ")[0]}</p>
        </div>
      </div>

      <div className="ap-kpi-grid">
        <KpiTile icon={<Users size={18} />} tone="info" label="Total Fresh Leads" value={data.total_leads} testid="stat-perf-total" />
        <KpiTile icon={<UserCheck size={18} />} tone="success" label="Converted" value={data.converted} sub={`${data.conversion_rate}%`} subTone="green" testid="stat-perf-converted" />
        <KpiTile icon={<Target size={18} />} tone="warning" label="Conversion Rate" value={`${data.conversion_rate}%`} testid="stat-perf-rate" />
        <KpiTile icon={<AlertCircle size={18} />} tone={data.overdue_followups > 0 ? "danger" : "gray"} label="Overdue Follow-ups" value={data.overdue_followups} sub={data.overdue_followups > 0 ? "action" : "clear"} subTone={data.overdue_followups > 0 ? "red" : "green"} testid="stat-perf-overdue" />
      </div>

      <div className="ap-layout">
        {data.by_program?.length > 0
          ? (
            <div className="ap-card">
              <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>Program Breakdown</h3>
              <div className="card-list">
                {data.by_program.map((p) => {
                  const prog = p.total ? Math.round((p.converted / p.total) * 100) : 0;
                  return (
                    <div key={p.program} className="program-row">
                      <div className="program-row-header">
                        <span className="program-name">{p.program}</span>
                        <span className="program-count">{p.total} leads</span>
                      </div>
                      <div className="progress-bar-wrap">
                        <div className="progress-bar" style={{ width: `${prog}%`, background: "var(--success)" }} />
                      </div>
                      <div className="program-row-footer">
                        <span className="text-green">{p.converted} converted</span>
                        <span className="text-red">{p.dropped} dropped</span>
                        <span className="text-muted">{prog}% conv.</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )
          : <div className="ap-card"><div className="empty-state">No program data yet.</div></div>}
        <ShareDonut title="Lead status mix" data={funnelData} centerLabel={data.total_leads} centerSub="leads" />
      </div>

      {data.overdue_followups > 0 && (
        <div className="alert-banner alert-warning" data-testid="overdue-alert" style={{ marginTop: 20 }}>
          <AlertCircle size={16} />
          <span>You have <strong>{data.overdue_followups}</strong> overdue follow-up{data.overdue_followups > 1 ? "s" : ""}. Go to Fresh Leads to take action.</span>
        </div>
      )}
    </div>
  );
}

// ─── COLLECTION / ADMIN DASHBOARD ────────────────────────────────────────────

function CollectionDashboard({ data, user }) {
  return (
    <div className="page-content" data-testid="dashboard-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-sub">Welcome back, {user?.name?.split(" ")[0]}</p>
        </div>
      </div>

      {/* ── 3 KPI cards ── */}
      <div className="ap-kpi-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>

        {/* Total Leads with stage strip */}
        <div className="kpi-card" data-testid="stat-total-leads" style={{ gridColumn: "span 1" }}>
          <div className="kpi-top">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="kpi-icon" style={{ background: TONE_BG.info, color: TONE_FG.info }}>
                <Users size={18} />
              </div>
              <span className="kpi-label">Total Leads</span>
            </div>
          </div>
          <div className="kpi-foot">
            <div className="kpi-value">{data.total_leads}</div>
          </div>
          <StageStrip byStage={data.by_stage} />
        </div>

        {data.total_balance !== undefined && (
          <KpiTile
            icon={<IndianRupee size={18} />}
            tone="warning"
            label="Balance Pending"
            value={compact(data.total_balance)}
            sub="total outstanding"
            subTone="orange"
            testid="stat-balance"
          />
        )}

        <KpiTile
          icon={<AlertCircle size={18} />}
          tone={data.overdue_followups > 0 ? "danger" : "gray"}
          label="Overdue Follow-ups"
          value={data.overdue_followups}
          sub={data.overdue_followups > 0 ? "action needed" : "all clear"}
          subTone={data.overdue_followups > 0 ? "red" : "green"}
          testid="stat-overdue"
        />
      </div>

      {/* ── Row 2: balanced 2-column layout — no dead space ── */}
      <div className="ap-layout" style={{ alignItems: "flex-start", marginTop: 14, gap: 14 }}>
        {/* Left column: calls chart + event breakdown table */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          <Calls7DayCard calls7d={data.calls_7d} />
          <EventBreakdownTable items={data.by_event} />
        </div>

        {/* Right column: priority follow-ups only */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          <PrioritySection
            todayFollowups={data.today_followups}
            upcomingFollowups={data.upcoming_followups}
          />
        </div>
      </div>

      {/* ── Row 3: Events — full-width wrapping strip at bottom ── */}
      <ActiveEventCards events={data.active_events} />
    </div>
  );
}

// ─── PAGE ENTRY ──────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isPerformer = user?.role === "performer_agent";
  const roleRedirect =
    user?.role === "finance" ? "/finance" : user?.role === "it" ? "/it" : null;

  useEffect(() => {
    if (roleRedirect) { setLoading(false); return; }
    const url = isPerformer ? "/performer/dashboard" : "/dashboard";
    api.get(url)
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isPerformer, roleRedirect]);

  if (roleRedirect) return <Navigate to={roleRedirect} replace />;
  if (loading) return <div className="loading-state">Loading dashboard...</div>;
  if (!data) return <div className="empty-state">Could not load dashboard.</div>;

  return isPerformer
    ? <PerformerDashboard data={data} user={user} />
    : <CollectionDashboard data={data} user={user} />;
}
